import { useEffect, useState } from 'preact/hooks';
import { ManagedFile } from '@components/media-manager';
import { parseAccept } from '@components/media-manager/use-media-manager';
import { FileThumbnail } from '@components/media-manager/file-thumbnail';
import { rpx } from 'client/lib/rpx-client';
import { isAudio, isImage, isVideo } from 'shared/media';
import { useIntl } from 'shared/intl/use-intl';

type Props = {
  accept?: string;
  onPick(file: ManagedFile): void;
};

type Status = 'loading' | 'ready' | 'failed';

export const RECENT_LIMIT = 6;

function useRecentFiles(accept?: string) {
  const [status, setStatus] = useState<Status>('loading');
  const [files, setFiles] = useState<ManagedFile[]>([]);

  useEffect(() => {
    let isCurrent = true;
    rpx.mediaManager
      .getManagedFiles({
        archived: false,
        sortBy: 'recent',
        limit: RECENT_LIMIT,
        mimePrefixes: parseAccept(accept),
      })
      .then((result) => {
        if (isCurrent) {
          setFiles(result.items);
          setStatus('ready');
        }
      })
      .catch(() => {
        if (isCurrent) {
          setStatus('failed');
        }
      });
    return () => {
      isCurrent = false;
    };
  }, [accept]);

  return { status, files };
}

function useKindLabel() {
  const intl = useIntl();
  return (type: string) => {
    if (isImage(type)) {
      return intl('Image');
    }
    if (isVideo(type)) {
      return intl('Video');
    }
    if (isAudio(type)) {
      return intl('Audio');
    }
    return intl('Document');
  };
}

export function RecentStrip({ accept, onPick }: Props) {
  const intl = useIntl();
  const kindLabel = useKindLabel();
  const { status, files } = useRecentFiles(accept);

  if (status === 'loading' || !files.length) {
    return;
  }

  return (
    <section class="mt-6" aria-labelledby="add-media-recent">
      <h3 id="add-media-recent" class="mb-3 text-sm font-semibold text-gray-900">
        {intl('Recently used')}
      </h3>
      <ul class="flex gap-3 overflow-x-auto pb-1 sm:grid sm:grid-cols-6 sm:overflow-visible sm:pb-0">
        {files.map((file) => (
          <li key={file.id} class="w-24 shrink-0 min-w-0 sm:w-auto">
            <button
              type="button"
              class="group flex flex-col w-full min-w-0 text-left rounded-md focus:outline-none"
              onClick={() => onPick(file)}
            >
              <FileThumbnail
                type={file.type}
                name={file.name}
                publicUrl={file.publicUrl}
                posterUrl={file.posterUrl}
                class="w-full h-16 rounded-md border border-gray-200 group-hover:border-indigo-500 group-focus-visible:border-indigo-500 group-focus-visible:ring-2 group-focus-visible:ring-indigo-100"
              />
              <span class="mt-1.5 block truncate text-xs text-gray-700">{file.name}</span>
              <span class="block truncate text-xs text-gray-400">{kindLabel(file.type)}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
